// @mops/pwa-v1/react — 업데이트 대기 · 서버 연결 안 됨을 React 훅과 안내 판으로.
//
// 문장은 **서비스 사전**이 준다(text). 공통 모듈이 문장을 들고 있으면 서비스 사전 규칙(한 곳 · 합니다체)을
// 건너뛴 화면 문구가 생긴다. 모양은 design-tokens v3 의 .b-toasts · .b-toast · .b-btn 어휘다(check.mjs 가
// 이 파일이 부르는 클래스가 v3 에 있는지 본다 — 없는 이름은 에러 없이 모양이 사라진다).
//
// react 는 peerDependency 다(서비스 것을 쓴다). 서비스의 vite.config 에 resolve.dedupe: ['react'] 가 있어야
// 서브모듈·vendor 경로에서 react 를 찾는다.
import { Fragment, createElement, useEffect, useState } from "react";
import { applyUpdate, hasWaiting, subscribeWaiting } from "./register.js";
import { probeReachable, watchReachable } from "./connectivity.js";

/** 새 판이 기다리고 있나 — 바뀌면 다시 그린다. */
export function usePwaUpdate() {
  const [waiting, setWaiting] = useState(hasWaiting);
  useEffect(() => subscribeWaiting(() => setWaiting(hasWaiting())), []);
  return waiting;
}

/**
 * `reachable` 이 `null` 이면 **아직 첫 확인 전** — 그 순간엔 안내를 띄우지 않는다(「끊겼다」와 「모른다」는 다르다).
 * @param {string} healthUrl
 * @param {{ intervalMs?: number }} [opts]
 */
export function useServerReachable(healthUrl, opts) {
  const [reachable, setReachable] = useState(/** @type {boolean | null} */ (null));
  const [checking, setChecking] = useState(false);
  const intervalMs = opts && opts.intervalMs;
  useEffect(() => watchReachable(healthUrl, setReachable, intervalMs ? { intervalMs } : undefined), [healthUrl, intervalMs]);
  function recheck() {
    setChecking(true);
    // 다시 시도하는 동안에도 지금 띄운 안내는 안 사라진다(깜빡이지 않는다).
    probeReachable(healthUrl)
      .then(setReachable)
      .finally(() => setChecking(false));
  }
  return { reachable, checking, recheck };
}

/**
 * 띄울 안내 목록 — 순수 함수라 React 없이 시험한다. 둘은 서로 무관해 **둘 다** 뜰 수 있다.
 * @param {{ updateWaiting: boolean, reachable: boolean | null }} state
 * @param {{ update: string, unreachable: string, refresh: string, retry: string }} text
 */
export function noticeItems({ updateWaiting, reachable }, text) {
  const out = [];
  if (updateWaiting) out.push({ key: "update", tone: "info", text: text.update, action: text.refresh });
  if (reachable === false) out.push({ key: "unreachable", tone: "warning", text: text.unreachable, action: text.retry });
  return out;
}

/**
 * 안내 판의 **모양만** — 상태는 받는다. 실물 `PwaNotice` 는 SW · 서버 상태에 묶여 있어 패턴 페이지가 상태를
 * 골라 세울 수 없다. 그 견본을 서비스가 마크업째 다시 쓰면 사본이 되므로 보기를 따로 내보낸다.
 * `stack: false` 면 `.b-toasts` 틀 없이 토스트만 낸다(패턴 페이지처럼 자리를 서비스가 정할 때).
 * `checking` 이면 「다시 시도」 단추가 비활성 + 도는 고리(`.b-spinner`)다 — 눌렸다는 것이 보여야 다시 누르지 않는다.
 * @param {{
 *   items: ReturnType<typeof noticeItems>,
 *   icons?: { info?: import("react").ReactNode, warning?: import("react").ReactNode },
 *   checking?: boolean,
 *   onUpdate?: () => void,
 *   onRetry?: () => void,
 *   stack?: boolean,
 * }} props
 */
export function PwaNoticeView({ items, icons, checking = false, onUpdate, onRetry, stack = true }) {
  if (items.length === 0) return null;
  const toasts = items.map((it) => {
    const busy = it.key === "unreachable" && checking;
    return createElement(
      "div",
      { key: it.key, className: `b-toast b-toast-${it.tone}`, role: it.tone === "warning" ? "alert" : "status" },
      icons && icons[it.tone] ? createElement("span", { className: "b-toast-icon" }, icons[it.tone]) : null,
      createElement("div", { className: "b-toast-text" }, createElement("p", { className: "b-toast-title" }, it.text)),
      createElement(
        "button",
        {
          type: "button",
          className: `b-btn b-btn-sm ${it.tone === "warning" ? "b-btn-secondary" : "b-btn-primary"}`,
          onClick: it.key === "update" ? onUpdate : onRetry,
          disabled: busy,
          "aria-busy": busy || undefined,
        },
        busy ? createElement("span", { className: "b-spinner is-sm", "aria-hidden": "true" }) : null,
        it.action,
      ),
    );
  });
  return stack ? createElement("div", { className: "b-toasts" }, toasts) : createElement(Fragment, null, toasts);
}

/**
 * 앱 루트 한 곳에 세운다. 문장은 서비스 사전에서 꺼내 넘긴다 — 공통 낱말 `common.action.refresh` ·
 * `common.action.retry`(@mops/i18n-v1) 가 단추 이름이다.
 * @param {{
 *   healthUrl: string,
 *   text: { update: string, unreachable: string, refresh: string, retry: string },
 *   icons?: { info?: import("react").ReactNode, warning?: import("react").ReactNode },
 * }} props
 */
export function PwaNotice({ healthUrl, text, icons }) {
  const updateWaiting = usePwaUpdate();
  const { reachable, checking, recheck } = useServerReachable(healthUrl);
  const items = noticeItems({ updateWaiting, reachable }, text);
  return createElement(PwaNoticeView, { items, icons, checking, onUpdate: applyUpdate, onRetry: recheck });
}
